import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { FileWithPath } from 'react-dropzone'
import type { IApiError } from '~/types/api'

import { type ICard } from '..'

import Button from '~/components/Buttons/Button'
import Dropzone from '~/components/Dropzone'
import FilePreview from '~/components/FilePreview'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { AUTHORIZATIONS, FORM_ERRORS, TOAST_ERRORS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useCreatePostFromFileMutation } from '~/queries/catalog'
import { useCreateFileMutation, useFileDetailsQuery, useUploadFileMutation } from '~/queries/files'
import { displayToast } from '~/utils/toast'

interface FormValues {
  id: string
}

const validationSchema = Yup.object().shape({
  id: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const defaultValues: FormValues = {
  id: '',
}

interface Props {
  addToCards: (newCard: ICard) => void
}

const CreateFromFileForm = ({ addToCards }: Props): ReactElement => {
  /** Local state */

  const isUserWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.CCC.CATALOG.WRITE])

  const [currentCard, setCurrentCard] = useState<ICard>()

  const [file, setFile] = useState<FileWithPath>()

  const [previewFileId, setPreviewFileId] = useState('')

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    control,
    formState: { errors, isDirty, isSubmitting, isValid },
    handleSubmit,
    register,
    reset,
  } = methods

  /** Queries & Mutations */

  const { data: fileDetailsData } = useFileDetailsQuery(previewFileId)

  const { mutateAsync: createFileMutation } = useCreateFileMutation()

  const { mutateAsync: createPostFromFileMutation } = useCreatePostFromFileMutation()

  const { mutateAsync: uploadFileMutation } = useUploadFileMutation()

  /** Handlers */

  const handleDrop = (value: FileWithPath[]) => setFile(value[0])

  const handleRemove = () => setFile(undefined)

  const handleFormSubmit = async (values: FormValues) => {
    if (!isUserWriteAuthorized) {
      displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
      return
    }

    if (file && isValid) {
      try {
        const { id, upload_url } = await createFileMutation({
          mimeType: file.type || 'application/octet-stream',
          name: file.name,
          size: file.size,
        })

        await uploadFileMutation({
          file: file,
          uploadUrl: upload_url,
        })

        const post = await createPostFromFileMutation({ id: values.id, pdf_file_id: id })

        setCurrentCard({
          categories: post.categories || [],
          coloringTemplateId: post.coloring_template_id,
          pdfFileId: post.pdf_file_id,
          previewPngFileId: post.preview_png_file_id,
          templateOrigin: post.origin_type,
        })
        setPreviewFileId(post.preview_png_file_id)
        setFile(undefined)
        reset()

        displayToast('Post successfully created', 'success')
      } catch (e) {
        captureException(e)

        if (isAxiosError(e)) {
          const errorData = e.response?.data as IApiError

          switch (errorData.kind) {
            case 'already_exists':
              displayToast('A post already exists with this ID', 'error')
              break
            case 'missing_field':
              displayToast('ID is missing', 'error')
              break
            default:
              displayToast('Post could not be created', 'error')
              break
          }
        }
      }
    }
  }

  /** Effects */

  useEffect(() => {
    if (currentCard && fileDetailsData) {
      addToCards({ ...currentCard, mediaUrl: fileDetailsData.download_url })
      setCurrentCard(undefined)
    }
  }, [addToCards, currentCard, fileDetailsData])

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Dropzone accept={{ 'application/pdf': [] }} className="create-from-file__dropzone" onDrop={handleDrop} />
        <fieldset className="create-from-file__file-details">
          {file ? (
            <FilePreview
              className="create-from-file__file-details__file-preview"
              mimeType={file.type}
              name={file.path || ''}
              onRemove={handleRemove}
              src={URL.createObjectURL(file)}
              withQualityIdentifier={false}
            />
          ) : null}
          <div className="create-from-file__file-details__id-area">
            <TextInput
              {...register('id')}
              className="create-from-file__file-details__id-area__id-input"
              error={errors.id?.message}
              placeholder="Enter an ID"
              readOnly={isSubmitting}
            />
            <Button className="submit-button" disabled={!isDirty || !file || isSubmitting} type="submit">
              {isSubmitting ? '⏳' : 'Submit'}
            </Button>
          </div>
        </fieldset>
      </form>
      <DevTool control={control} />
    </>
  )
}

export default CreateFromFileForm
